/* PJC v10 — Panier : le pied du tiroir (total + « Passer au paiement ») est toujours épinglé en CSS, la mesure du ratio du résumé disparaît ;
   l'état d'ouverture est publié pour la barre d'achat fixe : classe `pjc-cart-open` sur <html> + événement `pjc:cart-drawer` ({ open }). */
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/** Classe posée sur <html> tant que le tiroir panier est ouvert (lue par la barre d'achat fixe). */
const OPEN_CLASS = 'pjc-cart-open';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, focus trap, Escape, scroll lock, focus restore, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart and
 * publishing the open state for other components (sticky add-to-cart bar).
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    window.addEventListener('pageshow', this.#syncOpenState);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    this.#syncOpenState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    window.removeEventListener('pageshow', this.#syncOpenState);
    this.#publishOpenState(false);
  }

  /**
   * Publishes the open state: `html.pjc-cart-open` + `pjc:cart-drawer` ({ detail: { open } }) on document.
   * Idempotent: the open event is re-fired when the drawer is brought to front.
   * @param {boolean} open
   */
  #publishOpenState(open) {
    const root = document.documentElement;
    if (root.classList.contains(OPEN_CLASS) === open) return;

    root.classList.toggle(OPEN_CLASS, open);
    document.dispatchEvent(new CustomEvent('pjc:cart-drawer', { detail: { open } }));
  }

  /** Aligns the published state with the real one (restore after navigation, back/forward cache). */
  #syncOpenState = () => {
    this.#publishOpenState(Boolean(this.#themeDrawer?.hasAttribute('open')));
  };

  #handleDrawerOpen = () => {
    this.#publishOpenState(true);
  };

  #handleDrawerClose = () => {
    this.#publishOpenState(false);
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) return;

        const openDrawer = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openDrawer, { once: true });
        } else {
          openDrawer();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
